import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import InsideHeader from './InsideHeader';
import useInsideSeo from './useInsideSeo';

const initialForm = {
  nome: '',
  email: '',
  ruolo: '',
  titolo: '',
  racconto: '',
  anonimo: false,
  consenso: false,
};

export default function InsideStoryForm() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useInsideSeo({
    title: 'Raccontaci la tua storia',
    description: 'Invia a JKL / INSIDE una storia, un ruolo o un’esperienza dalla post-produzione televisiva, anche in forma anonima.',
    path: '/inside/raccontaci',
    image: '/inside-og/inside-home.png',
    noindex: true,
  });

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined, general: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.anonimo && !form.nome.trim()) next.nome = 'Inserisci il nome oppure scegli la pubblicazione anonima.';
    if (!form.email.trim()) next.email = 'Inserisci un indirizzo email.';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) next.email = 'L’indirizzo email non sembra valido.';
    if (!form.ruolo.trim()) next.ruolo = 'Indicaci il tuo ruolo.';
    if (!form.titolo.trim()) next.titolo = 'Scrivi un titolo o un argomento.';
    if (form.racconto.trim().length < 40) next.racconto = 'Raccontaci qualcosa in più: bastano anche poche righe, ma servono almeno 40 caratteri.';
    if (!form.consenso) next.consenso = 'Serve l’autorizzazione per poter leggere, valutare e rielaborare il contributo.';
    return next;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setSending(true);
    setErrors({});

    const displayName = form.anonimo ? 'Contributo anonimo' : form.nome.trim();
    const message = [
      'CONTRIBUTO PER JKL / INSIDE',
      '',
      `Nome: ${displayName}`,
      `Richiesta di anonimato in pubblicazione: ${form.anonimo ? 'Sì' : 'No'}`,
      `Ruolo: ${form.ruolo.trim()}`,
      `Titolo / argomento: ${form.titolo.trim()}`,
      '',
      'RACCONTO',
      form.racconto.trim(),
      '',
      'Autorizzazione al ricontatto e alla rielaborazione editoriale: Sì',
    ].join('\n');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: `[INSIDE] ${displayName}`,
          email: form.email.trim(),
          tipo: 'freelance',
          azienda: '',
          messaggio: message,
        }),
      });

      if (!response.ok) {
        let messageText = 'Non siamo riusciti a inviare il contributo. Riprova tra poco.';
        try {
          const payload = await response.json();
          if (payload?.error) messageText = payload.error;
        } catch {
          // La risposta potrebbe non essere JSON.
        }
        throw new Error(messageText);
      }

      setSent(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setErrors({ general: error.message || 'Invio non riuscito.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="inside-site inside-story-page">
      <InsideHeader compact />
      <main className="inside-story-shell">
        <section className="inside-story-intro">
          <Link to="/inside" className="inside-back">← Torna agli articoli</Link>
          <p className="inside-kicker">INSIDE / Redazione aperta</p>
          <h1>Raccontaci<br />la tua storia.</h1>
          <p>Un mestiere che viene raccontato male, una dinamica che conosce soltanto chi era in sala, un errore di workflow o un’esperienza che descrive davvero questo settore.</p>
          <div className="inside-story-monitor" aria-hidden="true">
            <div className="inside-story-monitor-head"><span>JKL_INSIDE_STORY</span><span>REC</span></div>
            <div className="inside-story-monitor-screen"><strong>YOUR<br />STORY</strong><i /></div>
            <div className="inside-story-monitor-track"><span /><span /><span /></div>
          </div>
          <small>L’invio non comporta la pubblicazione automatica. Potremmo contattarti per approfondire, verificare o rielaborare il racconto insieme.</small>
        </section>

        <section className="inside-story-form-panel">
          {sent ? (
            <div className="inside-story-success">
              <span>✓</span>
              <p className="inside-section-index">Contributo ricevuto</p>
              <h2>La storia è arrivata in redazione.</h2>
              <p>La leggeremo con attenzione. Ti contatteremo all’indirizzo <strong>{form.email}</strong> soltanto quando servirà approfondire o verificare qualche passaggio.</p>
              <Link to="/inside">Torna a JKL / INSIDE <b>↗</b></Link>
            </div>
          ) : (
            <form className="inside-story-form" onSubmit={handleSubmit} noValidate>
              <div className="inside-story-form-head">
                <p className="inside-section-index">Nuovo contributo</p>
                <span>I campi contrassegnati sono necessari.</span>
              </div>

              <label className="inside-story-field">
                <span>Nome {!form.anonimo && '*'}</span>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(event) => setField('nome', event.target.value)}
                  placeholder={form.anonimo ? 'Facoltativo: resterà comunque riservato' : 'Come possiamo chiamarti?'}
                  className={errors.nome ? 'error' : ''}
                />
                {errors.nome && <em>{errors.nome}</em>}
              </label>

              <label className="inside-story-check inside-story-anonymous">
                <input type="checkbox" checked={form.anonimo} onChange={(event) => setField('anonimo', event.target.checked)} />
                <span><strong>Preferisco restare anonimo</strong><small>Il nome non verrà pubblicato. L’email resterà visibile soltanto alla redazione.</small></span>
              </label>

              <div className="inside-story-row">
                <label className="inside-story-field">
                  <span>Email *</span>
                  <input type="email" value={form.email} onChange={(event) => setField('email', event.target.value)} placeholder="nome@email.it" className={errors.email ? 'error' : ''} />
                  {errors.email && <em>{errors.email}</em>}
                </label>
                <label className="inside-story-field">
                  <span>Ruolo *</span>
                  <input type="text" value={form.ruolo} onChange={(event) => setField('ruolo', event.target.value)} placeholder="Montatore, ingest, autore…" className={errors.ruolo ? 'error' : ''} />
                  {errors.ruolo && <em>{errors.ruolo}</em>}
                </label>
              </div>

              <label className="inside-story-field">
                <span>Titolo o argomento *</span>
                <input type="text" value={form.titolo} onChange={(event) => setField('titolo', event.target.value)} placeholder="Di cosa parla la tua storia?" className={errors.titolo ? 'error' : ''} />
                {errors.titolo && <em>{errors.titolo}</em>}
              </label>

              <label className="inside-story-field">
                <span>La tua storia *</span>
                <textarea value={form.racconto} onChange={(event) => setField('racconto', event.target.value)} placeholder="Non serve scrivere un articolo. Raccontaci i fatti, il contesto e ciò che secondo te vale la pena far conoscere." className={errors.racconto ? 'error' : ''} />
                <small>{form.racconto.length} caratteri</small>
                {errors.racconto && <em>{errors.racconto}</em>}
              </label>

              <label className={`inside-story-check${errors.consenso ? ' error' : ''}`}>
                <input type="checkbox" checked={form.consenso} onChange={(event) => setField('consenso', event.target.checked)} />
                <span><strong>Autorizzo JKL a ricontattarmi e a rielaborare il contributo per fini editoriali. *</strong><small>Prima di un’eventuale pubblicazione potranno essere effettuati tagli, verifiche e modifiche concordate.</small></span>
              </label>
              {errors.consenso && <em className="inside-story-error">{errors.consenso}</em>}
              {errors.general && <div className="inside-story-general-error">{errors.general}</div>}

              <button type="submit" className="inside-story-submit" disabled={sending}>
                {sending ? 'Invio in corso…' : 'Invia la storia'} <strong>↗</strong>
              </button>
              <p className="inside-story-direct-mail">Problemi con il form? Scrivi a <a href="mailto:info@joinjkl.com">info@joinjkl.com</a>.</p>
            </form>
          )}
        </section>
      </main>
      <footer className="inside-footer"><span>JKL / INSIDE</span><span>Post-produzione televisiva, dall’interno.</span><Link to="/inside">Articoli</Link></footer>
    </div>
  );
}
